import { inject, observer } from "mobx-react";
import moment from "moment";
import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import Done from "@material-ui/icons/Done";
import NotInterested from "@material-ui/icons/NotInterested";
import { lighten } from "@material-ui/core/styles/colorManipulator";
import { FREQUENCY_DISPLAY_NAMES } from "utils/constants";

// In order for sorting to work properly,
// id should equal the fieldName returned from sportyApi
const columnData = [
  {
    id: "name",
    disablePadding: true,
    label: "Name"
  },
  {
    id: "username",
    disablePadding: true,
    label: "Owner"
  },
  {
    id: "frequency",
    disablePadding: true,
    label: "Frequency"
  },
  {
    id: "enabled",
    disablePadding: true,
    label: "Enabled"
  },
  {
    id: "updatedAt",
    disablePadding: true,
    label: "Last modified"
  },
  {
    id: "createdAt",
    disablePadding: true,
    label: "Created at"
  }
];

class EnhancedTableHead extends React.Component {
  createSortHandler = property => event => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const {
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      rowCount
    } = this.props;

    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
            />
          </TableCell>
          {columnData.map(column => {
            return (
              <TableCell
                key={column.id}
                padding={column.disablePadding ? "none" : "default"}
                sortDirection={orderBy === column.id ? order : false}
              >
                <Tooltip
                  title="Sort"
                  placement={"bottom-start"}
                  enterDelay={300}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={order}
                    onClick={this.createSortHandler(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            );
          }, this)}
        </TableRow>
      </TableHead>
    );
  }
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired
};

const toolbarStyles = theme => ({
  root: {
    paddingRight: theme.spacing(1)
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85)
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark
        },
  spacer: {
    flex: "1 1 100%"
  },
  actions: {
    color: theme.palette.text.secondary
  },
  title: {
    flex: "0 0 auto"
  },
  loadRules: {
    position: "relative",
    right: "60px",
    top: "35px",
    width: "65px"
  }
});

let EnhancedTableToolbar = props => {
  const { numSelected, classes, rulesStore, authStore } = props;
  return (
    <Toolbar
      className={classNames(classes.root, {
        [classes.highlight]: numSelected > 0
      })}
    >
      <div className={classes.title}>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography variant="h6" id="tableTitle">
            {rulesStore.ruleTitle}
          </Typography>
        )}
      </div>
      <div className={classes.spacer} />
      <div className={classes.actions}>
        {authStore.canViewAllRules && (
          <div>
            <Typography
              className={classes.loadRules}
              color="inherit"
              variant="subtitle1"
            >
              My rules
            </Typography>
            <Checkbox
              checked={!rulesStore.allRules}
              onChange={() => rulesStore.toggleAllRules()}
            />
          </div>
        )}

        {numSelected > 0 && (
          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete"
              onClick={() => rulesStore.handleDelete()}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired
};

EnhancedTableToolbar = withStyles(toolbarStyles)(EnhancedTableToolbar);

const styles = theme => ({
  root: {
    width: "100%"
  },
  table: {
    minWidth: 1020
  },
  tableWrapper: {
    overflowX: "auto",
    maxHeight: "70vh"
  }
});

class EnhancedTable extends React.Component {
  componentDidMount() {
    const { rulesStore } = this.props;
    rulesStore.getRules();
  }

  render() {
    const { classes, rulesStore, authStore } = this.props;
    const {
      rowsPerPage,
      selected,
      order,
      orderBy,
      page,
      rules,
      totalRules
    } = rulesStore;

    return (
      <Paper className={classes.root}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          rulesStore={rulesStore}
          authStore={authStore}
        />
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={(e, checked) =>
                rulesStore.handleSelectAll(e, checked)
              }
              onRequestSort={(event, property) =>
                rulesStore.handleSortRequest(event, property)
              }
              rowCount={rules.length}
            />
            <TableBody>
              {rules.map(n => {
                const isSelected = rulesStore.isSelected(n.ruleId);
                return (
                  <TableRow
                    hover
                    onClick={e => {
                      window.location = `rules/${n.ruleId}/edit`;
                    }}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={-1}
                    key={n.ruleId}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onClick={e => e.stopPropagation()}
                        onChange={(e, change) =>
                          rulesStore.handleSelect(n.ruleId)
                        }
                      />
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none">
                      {n.name}
                    </TableCell>
                    <TableCell padding="none">{n.username}</TableCell>
                    <TableCell padding="none">
                      {FREQUENCY_DISPLAY_NAMES[n.frequency]}
                    </TableCell>
                    <TableCell padding="none">
                      {n.enabled ? <Done /> : <NotInterested />}
                    </TableCell>
                    <TableCell padding="none">
                      {moment(n.updatedAt.split("[")[0]).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell padding="none">
                      {moment(n.createdAt.split("[")[0]).format("MMM D, YYYY")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          component="div"
          count={totalRules}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            "aria-label": "Previous Page"
          }}
          nextIconButtonProps={{
            "aria-label": "Next Page"
          }}
          onPageChange={(event, page) => rulesStore.setPage(page)}
          onRowsPerPageChange={event =>
            rulesStore.setRowsPerPage(event.target.value)
          }
        />
      </Paper>
    );
  }
}

EnhancedTable.propTypes = {
  classes: PropTypes.object.isRequired
};

export default inject(
  "rulesStore",
  "authStore"
)(withStyles(styles)(observer(EnhancedTable)));



// WEBPACK FOOTER //
// ./src/components/RulesTable.js